/**
 * external dependencies
 */
import { TabulatorFull, Options, CellComponent } from 'tabulator-tables';

/**
 * internal dependencies
 */
import { formatDate } from '../../../helpers/helpers';
import { IndividualTransaction } from '../../../models/transactions.interface';

/**
 * 
 * @param table 
 * @param data 
 * @returns 
 */
export const displayTransactionTable = (table: HTMLElement, data: IndividualTransaction[]): TabulatorFull => {

  /**
   * Formats the link button displayed.
   * 
   * @param {CellComponent} cell the cell component
   * @param {any} formatterParams the formatter parameters
   * 
   * @returns the link button
   */ 
  const link_button = (cell: CellComponent, formatterParams: any) => {
    const { txnId, timeStampReq } = cell.getData();
    return `<a class='aafp_btn-table btn_theme-primary'
              href='/transactions/transaction-individual/?transactionID=${txnId}' 
              data-toggle="tooltip" 
              data-placement="bottom" 
              title="View transaction for ${timeStampReq}">
                <i class="far fa-eye"></i>
            </a>`;
  };

  /**
   * Formats the Terminal column.
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns the terminal name info
   */ 
  const formatTerminalName = (cell: CellComponent, formatterParams: any) => {
    const { terminalId, terminalName } = cell.getData();
    if (terminalId != terminalName) return `<div><p style="margin: 0 0 .15rem;">${terminalId}</p><p style="margin: 0 0 .15rem;">${terminalName ?? ""}</p></div>`;
    else return `<div><p style="margin: 0 0 .15rem;">${terminalId}</p></div>`;
  };

  /**
   * Formats the Terminal code/name for export.
   * 
   * @param {any} value the value of the cell
   * @param {any} data the data of the cell
   * @param {any} type the type of the cell
   * @param {any} component the component of the cell
   * @param {any} row the row of the cell
   * 
   * @returns the terminal name info
   */ 
  const formatTerminalNameExport = (value: any, data: any, type: any, component: any, row: any) => {
    const { terminalId, terminalName } = data;
    return terminalId != terminalName ? `${terminalId} - ${terminalName}` : terminalId;
  };

  /**
   * Formats the payment method displayed.
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns the payment method
   */ 
  const cardInfo = (cell: CellComponent, formatterParams: any) => {
    const { cardName, cardNumber } = cell.getData();
    return `<div><p style="margin: 0 0 .15rem;">${cardName ?? ""}</p><p style="margin: 0 0 .15rem;">${cardNumber ?? ""}</p></div>`;
  };

  /**
   * Formats the price per gallon.
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns the price per gallon
   */ 
  const formatGallonsPrice = (cell: CellComponent, formatterParams: any) => {
    const { pricePerGallon } = cell.getData();
    return pricePerGallon === "0" ? "$0.00" : `$${pricePerGallon}`;
  };

  /**
   * Formats the amount.
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns the formatted amount
   */ 
  const formatAmountPrice = (cell: CellComponent, formatterParams: any) => {
    const { paidDollars } = cell.getData();
    return paidDollars === 0 ? "$0.00" : `$${paidDollars}`;
  };

  /**
   * Formats the payment method displayed.
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns the formatted payment method
   */ 
  const paymentFormat = (cell: CellComponent, formatterParams: any) => {
    const { paymentMethod } = cell.getData();
    return paymentMethod === "0" ? "Pay At Pump" : "Invoice";
  };

  /**
   * Formats the payment method for export.
   * 
   * @param {any} value the value of the cell
   * @param {any} data the data of the cell
   * @param {any} type the type of the cell
   * @param {any} component the component of the cell
   * @param {any} row the row of the cell
   * 
   * @returns the formatted payment method
   */ 
  const paymentFormatExport = (value: any, data: any, type: any, component: any, row: any) => {
    return value === "0" ? "Pay At Pump" : "Invoice";
  };

  /**
   * Formats the date displayed.
   * 
   * @param {CellComponent} cell the cell component
   * @param {any} formatterParams the formatter parameters
   * 
   * @returns the formatted date
   */ 
  const dateFormat = (cell: CellComponent, formatterParams: any) => {
    const { timeStampReq } = cell.getData();
    const date = formatDate(timeStampReq);
    return date;
  };

  /**
   * Formats the date for export.
   * 
   * @param {any} value the value of the cell
   * @param {any} data the data of the cell
   * @param {any} type the type of the cell
   * @param {any} component the component of the cell
   * @param {any} row the row of the cell
   * 
   * @returns the formatted date
   */ 
  const dateFormatExport = (value: any, data: any, type: any, component: any, row: any) => {
    return formatDate(value);
  };

  /**
   * Formats the hose type displayed.
   * 
   * @param {CellComponent} cell the cell component
   * 
   * @returns the hose type
   */ 
  const hoseTypeFormat = (cell: CellComponent, formatterParams: any, onRendered: any) => {
    const { hoseId } = cell.getData();
    return hoseId === "0" ? "Autogas/Cylinder" : hoseId === "1" ? "Autogas" : "Cylinder Fill";
  };

  const options: Options = {
    index: "txnId",
    data: data,
    pagination: true,
    paginationMode: "local",
    paginationSize: 10,
    paginationSizeSelector: [
      25,
      50,
      100
    ],
    paginationCounter: "rows",
    selectableRows: false,
    initialSort: [
      {
        column: "timeStampReq",
        dir: "desc"
      }
    ],
    columns: [
      {
        title: "ID",
        field: "txnId",
        visible: false,
        cssClass: "table-cell-no-margins",
        frozen: true,
        download: false
      },
      {
        title: "",
        field: "LinkButton",
        formatter: link_button,
        width: 70,
        cssClass: "table-cell-right-margin",
        hozAlign: "center",
        resizable: true,
        frozen: true,
        headerSort: false,
        download: false
      },
      {
        title: "TerminalID",
        field: "terminalId",
        sorter: "string",
        visible: false
      },
      {
        title: "Terminal",
        field: "terminalName",
        formatter: formatTerminalName,
        accessorDownload: formatTerminalNameExport,
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Date",
        field: "timeStampReq",
        accessorDownload: dateFormatExport,
        formatter: dateFormat,
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Transaction ID",
        field: "txnId",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
    },
      {
        title: "Customer Name",
        field: "customerName",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Card Number",
        field: "cardNumber",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Card Name",
        field: "cardName",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Card Group",
        field: "cardGroupName",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Errors",
        field: "ErrorDescription",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Payment Type",
        field: "paymentMethod",
        formatter: paymentFormat,
        accessorDownload: paymentFormatExport,
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Driver",
        field: "driverNumber",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Driver Name",
        field: "driverName",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Vehicle",
        field: "vehicleNumber",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Odometer",
        field: "odometer",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Gallons",
        field: "deliveredGallons",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Price Per Gallon",
        field: "pricePerGallon",
        formatter: formatGallonsPrice,
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Amount",
        field: "paidDollars",
        formatter: formatAmountPrice,
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Payment Reference",
        field: "paymentReference",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Hose",
        field: "hoseId",
        formatter: hoseTypeFormat,
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Training",
        field: "trainingComplete",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Totalizer",
        field: "totalizer",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Partner",
        field: "partnerName",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      },
      {
        title: "Asked For",
        field: "askedFor",
        sorter: "string",
        resizable: true,
        vertAlign: "middle"
      }
    ]
  };

  const transactionTable = new TabulatorFull( table, options );
  return transactionTable;
};